import React from 'react';

import { Checkbox, FormControlLabel, MenuItem, Select, Stack, Tooltip } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { isValid, parseISO } from 'date-fns';

import { InputWrapper } from 'components/Form/InputWrapper';
import { IconWrapper } from 'components/Icon';

import { DATA_START, GRANULARITIES, PERIODS, autoGranularity, toApiDate } from '../utils/period';

const UNIQUE_HINT =
  'Logged-in visitors (including all in-app visits) are counted once per period. Anonymous website visitors can only be recognised within a single day, so one returning on several days counts once per day.';

export const StatisticsFilters = ({ filters, onChange, showUnique }) => {
  const from = parseISO(filters.from);
  const to = parseISO(filters.to);

  const handlePeriodChange = ({ target: { value } }) => {
    if (value === 'custom') {
      onChange({ period: value, from: filters.from, to: filters.to, granularity: filters.granularity });
      return;
    }
    onChange({ period: value, from: null, to: null, granularity: null });
  };

  const handleDateChange = (key) => (date) => {
    if (!date || !isValid(date)) return;
    const range = { from, to, [key]: date };
    onChange({ [key]: toApiDate(date), granularity: autoGranularity(range.from, range.to) });
  };

  return (
    <Stack direction="row" gap={2} flexWrap="wrap" alignItems="flex-end">
      <InputWrapper label="Period" wrapperSx={{ width: 160 }}>
        <Select value={filters.period} onChange={handlePeriodChange} inputProps={{ 'aria-label': 'Period' }}>
          {PERIODS.map(({ value, label }) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </InputWrapper>

      {filters.period === 'custom' && (
        <>
          <InputWrapper label="Start date" wrapperSx={{ width: 170 }}>
            <DatePicker
              value={from}
              minDate={DATA_START}
              maxDate={to}
              onChange={handleDateChange('from')}
              slotProps={{ textField: { inputProps: { 'aria-label': 'Start date' } } }}
            />
          </InputWrapper>
          <InputWrapper label="End date" wrapperSx={{ width: 170 }}>
            <DatePicker
              value={to}
              minDate={from}
              disableFuture
              onChange={handleDateChange('to')}
              slotProps={{ textField: { inputProps: { 'aria-label': 'End date' } } }}
            />
          </InputWrapper>
          <InputWrapper label="Group by" wrapperSx={{ width: 130 }}>
            <Select
              value={filters.granularity}
              onChange={({ target: { value } }) => onChange({ granularity: value })}
              inputProps={{ 'aria-label': 'Group by' }}
            >
              {GRANULARITIES.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </InputWrapper>
        </>
      )}

      {showUnique && (
        <Stack direction="row" alignItems="center" minHeight={47}>
          <FormControlLabel
            sx={{ mr: 0.5 }}
            control={<Checkbox checked={filters.unique} onChange={({ target: { checked } }) => onChange({ unique: checked ? '1' : null })} />}
            label="Unique visitors"
          />
          <Tooltip title={UNIQUE_HINT}>
            <span>
              <IconWrapper icon="info" sx={{ svg: { strokeWidth: 1.5 } }} aria-label="About unique visitors" />
            </span>
          </Tooltip>
        </Stack>
      )}
    </Stack>
  );
};
